import { Company } from 'module/api/company/types';
import { useParams } from '@rocketlane/rocket-router';
import { Document } from 'api/pages';
import { CompanyPage } from 'api/pages/types';
import { useGetDocumentQuery } from 'queries/pages';
import { useEffect, useCallback } from 'react';
import { useAccountSpacesRouteNavigations } from './route-navigations';

interface Props {
  company: Company;
  companyPages: CompanyPage[];
  isCompanyPagesLoading: boolean;
  isCreationMode?: boolean;
  projectId?: number;
  isModuleFed?: boolean;
}
interface Result {
  spaceDocument?: Document;
  isSpaceDocumentLoading: boolean;
  refetchSpaceDocument: () => void;
}
export const useCurrentSpaceDocument = ({
  company,
  companyPages,
  isCompanyPagesLoading,
  isCreationMode = false,
  projectId,
  isModuleFed,
}: Props): Result => {
  const {
    spaceDocumentId: spaceDocumentIdParam,
    nitroPageId: nitroPageIdParam,
  } = useParams<{ spaceDocumentId?: string; nitroPageId?: string }>();
  const { navigateToSpaceDocument } = useAccountSpacesRouteNavigations(
    projectId,
    isModuleFed,
  );

  // Use nitroPageId if isModuleFed is true or projectId is provided; otherwise use spaceDocumentId
  const documentIdParam =
    isModuleFed || projectId != null ? nitroPageIdParam : spaceDocumentIdParam;

  // Check if we're in creation mode (documentIdParam is "new")
  const isNew = documentIdParam === 'new';
  const spaceDocumentId = Number(documentIdParam);
  const isSpaceDocumentIdValid = !isNaN(spaceDocumentId);

  const isQueryEnabled =
    isSpaceDocumentIdValid && !isCreationMode && !isNew && spaceDocumentId > 0;

  const {
    data: spaceDocument,
    isLoading: isSpaceDocumentLoadingRaw,
    refetch: refetchSpaceDocument,
  } = useGetDocumentQuery(isSpaceDocumentIdValid ? spaceDocumentId : -1, {
    enabled: isQueryEnabled,
    retry: false,
  });

  // Explicitly set loading to false when query is disabled
  const isSpaceDocumentLoading = isQueryEnabled
    ? isSpaceDocumentLoadingRaw
    : false;
  const navigateToFirstSpaceDocument = useCallback(() => {
    if (companyPages.length > 0) {
      const companyIdForNavigation = projectId ?? company.companyId;

      navigateToSpaceDocument(
        companyIdForNavigation,
        companyPages[0]?.documentId,
      );
    }
  }, [navigateToSpaceDocument, company.companyId, companyPages, projectId]);

  useEffect(() => {
    // Don't auto-navigate if we're loading, in creation mode, or URL is "new"
    if (isCompanyPagesLoading || isCreationMode || isNew) {
      return;
    }
    if (!isSpaceDocumentIdValid) {
      navigateToFirstSpaceDocument();
    }
  }, [
    isCompanyPagesLoading,
    isSpaceDocumentIdValid,
    navigateToFirstSpaceDocument,
    isCreationMode,
    isNew,
  ]);
  useEffect(() => {
    // Don't auto-navigate if we're loading, in creation mode, or URL is "new"
    if (
      isSpaceDocumentLoading ||
      isCompanyPagesLoading ||
      isCreationMode ||
      isNew
    ) {
      return;
    }
    if (isSpaceDocumentIdValid && spaceDocument == null) {
      navigateToFirstSpaceDocument();
    }
  }, [
    isSpaceDocumentLoading,
    isCompanyPagesLoading,
    isSpaceDocumentIdValid,
    spaceDocument,
    navigateToFirstSpaceDocument,
    isCreationMode,
    isNew,
  ]);

  return {
    spaceDocument,
    isSpaceDocumentLoading,
    refetchSpaceDocument,
  };
};
